import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Loader2, ShieldCheck, Lock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API, errorText, formatINR, quotationStatusColors } from "@/lib/payments";

const ItemsTable = ({ items }) => (
  <table className="w-full text-sm" data-testid="pay-items-table">
    <thead><tr className="text-left text-stone-500 border-b border-stone-200">
      <th className="py-2 font-normal">Item</th><th className="py-2 font-normal text-right">Qty</th>
      <th className="py-2 font-normal text-right">Unit</th><th className="py-2 font-normal text-right">Total</th>
    </tr></thead>
    <tbody>
      {items.map((it, i) => (
        <tr key={i} className="border-b border-stone-100">
          <td className="py-3 text-stone-800">{it.description}</td>
          <td className="py-3 text-right">{it.quantity.toLocaleString("en-IN")}</td>
          <td className="py-3 text-right">{formatINR(it.unit_price_paise)}</td>
          <td className="py-3 text-right">{formatINR(it.line_total_paise)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PayAction = ({ q, token }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/pay/${token}/start`);
      window.location.assign(res.data.redirect_url);
    } catch (err) {
      setError(errorText(err, "Could not start the payment"));
      setBusy(false);
    }
  };
  if (q.status === "paid") return <p className="text-green-700 font-medium" data-testid="pay-already-paid">This quotation has been paid. Thank you.</p>;
  if (q.status !== "approved") return <p className="text-stone-600" data-testid="pay-unavailable">This quotation is {q.status.replace("_", " ")} and cannot be paid online.</p>;
  if (!q.payments_enabled) return (
    <p className="flex gap-2 text-amber-800 bg-amber-50 rounded-lg p-3 text-sm" data-testid="pay-disabled-notice">
      <Info className="h-4 w-4 mt-0.5 shrink-0" />Online payment will be available shortly. Please contact Prithira Global to complete this order.
    </p>
  );
  return (
    <div className="space-y-3">
      <Button size="lg" className="w-full rounded-full" onClick={pay} disabled={busy} data-testid="pay-with-phonepe-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
        Pay {formatINR(q.amount_paise)} with PhonePe
      </Button>
      {error && <p className="text-sm text-red-600" data-testid="pay-error">{error}</p>}
    </div>
  );
};

export default function PayPage() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pay/${token}`).then((r) => setQ(r.data)).catch(() => setNotFound(true));
  }, [token]);

  return (
    <section className="bg-stone-50 min-h-[70vh] pt-32 md:pt-40 lg:pt-48 pb-16 px-4" data-testid="pay-page">
      <Helmet title="Quotation payment | Prithira Global" meta={[{ name: "robots", content: "noindex, nofollow" }]} />
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-stone-200 p-6 sm:p-10">
        {notFound ? <p className="text-stone-600 text-center" data-testid="pay-not-found">This payment link is invalid or no longer available.</p>
          : !q ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          : (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500">Quotation</p>
                  <h1 className="font-heading text-3xl text-stone-900" data-testid="pay-quotation-number">{q.number}</h1>
                  <p className="text-stone-600 mt-1">{q.customer_name}{q.company_name && ` • ${q.company_name}`}</p>
                </div>
                <Badge className={quotationStatusColors[q.status]} data-testid="pay-quotation-status">{q.status.replace("_", " ")}</Badge>
              </div>
              <ItemsTable items={q.items} />
              <div className="flex justify-between items-baseline">
                <span className="text-stone-500">Total payable</span>
                <span className="font-heading text-3xl text-stone-900" data-testid="pay-total">{formatINR(q.amount_paise)}</span>
              </div>
              {q.notes && <p className="text-sm text-stone-600 bg-stone-50 rounded-lg p-3 whitespace-pre-line">{q.notes}</p>}
              <PayAction q={q} token={token} />
              {q.payments_enabled && q.status === "approved" && <p className="flex items-center gap-2 text-xs text-stone-400"><ShieldCheck className="h-4 w-4" />Secure payment processed by PhonePe. Prithira Global never sees your card or UPI details.</p>}
            </div>
          )}
      </div>
    </section>
  );
}
